<script lang="ts">
    
    import type { Form } from "../Model/Form";
    import type { Section } from "../Model/Section";
    import SectionForm from "../components/sectionForm.svelte";
    import { setContext } from "svelte";
    import { readable, writable } from "svelte/store";
    import type { Readable } from "svelte/store";
    import { formsService, instancesService, sectionsFactory } from "../services";
    import { navigate } from "svelte-navigator";
    import { Button, Icon, Input, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "sveltestrap";
    import SaveBtn from "../components/SaveBtn.svelte";
    import AppWrapper from "../components/AppWrapper.svelte";
    import FormPreview from "../components/FormPreview/FormPreview.svelte";

    export let formId = null;

    const _expandAll = writable(false);
    const expandAll = readable(false, set => _expandAll.subscribe(set));
    const _readonly = writable(false);
    const readonly = readable(false, set => _readonly.subscribe(set));
    
    let isSaving = false;
    let isLoaded = false;
    let hasInstance = false;
    let passingToProduction = false;
    let passingToDeveloping = false;
    let openProductionModal = false;
    let openDevelopingModal = false;
    let form: Form | null = null;
    let sections: Readable<Section[]>;

    setContext('expandAll', expandAll);
    setContext('readonly', readonly);

    $: loadForm(formId);

    async function loadForm(formId: string): Promise<void> {
        if (!formId) {
            navigate('/');
            return;
        }
        form = await formsService.findOne(formId);

        hasInstance = await instancesService.isInstance(formId)

        sections = form.sectionsReadable;
        _readonly.set(form.production);
        isLoaded = true;
    }

    function newSection(){
        const section = sectionsFactory.createNewSection(form);
        section.position.set($sections.length + 1);
        form.addSection(section);
    }

    function downSection(section: Section): void {
		form.moveDown(section);
    }

    function upSection(section: Section): void {
        form.moveUp(section);
    }

    function removeSection(section: Section){
        form.quitSection(section.id);
    }

    async function onSave(){
        if ($readonly || isSaving) {
            return;
        }
        try {
            isSaving = true;
            await formsService.saveForm(form);
        } catch (error) {
            console.error(error);
        } finally {
            isSaving = false;
        }
    }

    async function passToDeveloping(){
        if (passingToDeveloping) {
            return;
        }
        try {
            passingToDeveloping = true;
            await formsService.passToDeveloping(form.id);
            form.production = false;
            _readonly.set(false);
            openDevelopingModal = false;
        } catch (error) {
            console.error(error);
        } finally {
            passingToDeveloping = false;
        }
    }

    async function passToProduction(){
        if (passingToProduction) {
            return;
        }
        try {
            passingToProduction = true;
            await formsService.passToProduction(form.id);
            form.production = true;
            _readonly.set(true);
            openProductionModal = false;
        } catch (error) {
            console.error(error);
        } finally {
            passingToProduction = false;
        }
    }

    function changePartner(){

    }

    function onToggleExpandAll(): void {
        $_expandAll = !$_expandAll;
    }

    function toggleProductionModal(): void {
        openProductionModal = !openProductionModal;
    }

    function toggleDevelopingModal(): void {
        openDevelopingModal = !openDevelopingModal;
    }

</script>

<style lang="scss">

    .partner-section{
        display: flex;
        justify-content: end;
        padding-top: 10px;
    }

    .sections {
        display: flex;
        flex-wrap: wrap;
        align-items: flex-start;
        margin-left: -1rem;
    }

    .container-fluid {
        margin-top: -1rem;
        padding-top: 1rem;
        padding-bottom: 1rem;
        flex: 1 1 0;
        min-height: 0;
        box-sizing: border-box;
    }

    .main-row {
        height: 100%;
    }

    .main-col {
        overflow: auto;
        height: 100%;
    }

</style>

<AppWrapper>

    <div class="container-fluid">
        <div class="row main-row">
            <div class="col-8 main-col">
                <div class="row">
                    <div class="col-6">
                        <h3>Editar formulario</h3>
                    </div>
                    <div class="col-6 text-end">
                        {#if !form?.production}
                            <Button color="warning" type="button" on:click={toggleProductionModal}>
                                <Icon name="shield-lock" /> Pasar a producción
                            </Button>
                            <SaveBtn on:save={onSave} {isSaving} />
                            {#if form}
                                <div class="partner-section">
                                    <Input id="c1" type="checkbox" bind:checked={form.bodyPartner} on:change={changePartner} label="BuddyPartner" />
                                </div>
                            {/if}
                        {/if}
                        {#if !hasInstance && form?.production}
                        <Button color="warning" type="button" on:click={toggleDevelopingModal}>
                            <Icon name="shield-lock" /> Pasar a desarrollo
                        </Button>
                    {/if}
                    </div>
                </div>
                {#if isLoaded}
                    <div class="row">
                        <div class="col-12">
                            <div class="mb-3">
                                <label class="form-label" for="form-name">Título</label>
                                <input type="text" class="form-control" id="form-name" bind:value={form.name}>
                            </div>
                            <div class="mb-3">
                                <label for="form-description" class="form-label">Descripción</label>
                                <textarea class="form-control" id="form-description" rows="3" bind:value={form.description}></textarea>
                            </div>
                        </div>
                    </div> 
                
            
                {#if $sections.length == 0}
                    <div class="row">
                        <div class="col-12 text-center">
                            <button class="btn btn-primary btn-sm" type="button" on:click={newSection}>Añadir Sección</button>
                        </div>
                    </div>  
                {:else}
                    <div class="row">
                        <div class="col-8">
                            <h4>Listado de Secciones</h4>
                        </div>
                        <div class="col-4 text-end">
                            <div class="btn-group cell-order-button" role="group" aria-label="Basic outlined example">
                                <Button type="button" size="sm" on:click={onToggleExpandAll}>
                                    {#if $expandAll}
                                        <Icon name="arrows-collapse" /> Contraer todo
                                    {:else}
                                        <Icon name="arrows-expand" /> Expandir todo
                                    {/if}
                                </Button>
                            </div>
                        </div>
                    </div>
                    
                     
                    <div class="sections">
                        {#each $sections as section}
                            <SectionForm {section}
                                on:downPosition={() => downSection(section)} 
                                on:upPosition={() => upSection(section)}
                                on:remove={() => removeSection(section)} />
                        {/each} 
                    </div>
                   
                    {#if !form.production}
                        <div class="row mt-3">
                            <div class="col-12 text-center">
                                <Button type="button" color="primary" size="sm" on:click={newSection}>
                                    <Icon name="plus" /> Añadir Sección
                                </Button>
                            </div>
                        </div> 
            
                        <div class="footer-form">
                            <hr>
                            <div class="row">
                                <div class="col-12 text-end">
                                    <SaveBtn on:save={onSave} {isSaving} />
                                </div>
                            </div>
                        </div>
                    {/if}
                    {/if}
            
                {/if}
            </div>
            <div class="col-4 main-col">
                {#if form}
                    <h4>Previsualización</h4>
                    <FormPreview {form} />
                {/if}
            </div>
        </div>
    </div>

</AppWrapper>

<Modal isOpen={openProductionModal} toggle={toggleProductionModal}>
    <ModalHeader toggle={toggleProductionModal}>
        <Icon name="shield-lock" /> Pasar a producción
    </ModalHeader>
    <ModalBody>
        <p>Está a punto de pasar a producción este formulario.<br>El formulario no podrá recibir mas modificaciones aunque aún podrá realizar copias del mismo que sí serán modificables.</p>
        <p>¿Seguro que desea continuar?</p>
    </ModalBody>
    <ModalFooter class="justify-content-center">
        <Button type="button" color="warning" on:click={passToProduction} disabled={passingToProduction}>
            {#if passingToProduction}
                <Spinner size="sm" /> Pasando a producción
            {:else}
                <Icon name="shield-lock" /> Sí, continuar
            {/if}
        </Button>
    </ModalFooter>
</Modal>

<Modal isOpen={openDevelopingModal} toggle={toggleDevelopingModal}>
    <ModalHeader toggle={toggleDevelopingModal}>
        <Icon name="shield-lock" /> Pasar a desarrollo
    </ModalHeader>
    <ModalBody>
        <p>Está a punto de pasar a desarrollo este formulario.</p>
        <p>¿Seguro que desea continuar?</p>
    </ModalBody>
    <ModalFooter class="justify-content-center">
        <Button type="button" color="warning" on:click={passToDeveloping} disabled={passingToDeveloping}>
            {#if passingToDeveloping}
                <Spinner size="sm" /> Pasando a desarrollo
            {:else}
                <Icon name="shield-lock" /> Sí, continuar
            {/if}
        </Button>
    </ModalFooter>
</Modal>

